/**
 * Value equality for activity-log diffing.
 *
 * Services decide whether to write an activity-log entry by comparing the old
 * and new value of a field. A bare `!==` gets this wrong in two ways that both
 * produce phantom log entries reading "None → None" or "Jul 27 → Jul 27":
 *
 *   - Empty is not one value. `null`, `undefined` and `''` all render as "None"
 *     but are three distinct values to `!==`.
 *   - Dates are objects. A Firestore Timestamp and a Date for the same instant
 *     are never `===`, so every save of an unchanged date logs a "change".
 */

const isEmpty = (value) => value === null || value === undefined || value === '';

// Firestore Timestamps, JS Dates, and anything else carrying a toMillis/toDate.
function toMillis(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return null;
}

/**
 * True when two field values represent the same thing and no activity-log
 * entry should be written.
 */
export function valuesEqual(a, b) {
  if (a === b) return true;
  if (isEmpty(a) && isEmpty(b)) return true;
  if (isEmpty(a) !== isEmpty(b)) return false;

  const aMs = toMillis(a);
  const bMs = toMillis(b);
  if (aMs !== null && bMs !== null) return aMs === bMs;
  // One side is a date and the other isn't — genuinely different.
  if (aMs !== null || bMs !== null) return false;

  // Arrays compare by contents, not order: reordering assignees or event ids
  // isn't a change worth a log line.
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v) => b.includes(v)) && b.every((v) => a.includes(v));
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;

  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false; // circular or otherwise unserializable — treat as changed
    }
  }

  return false;
}
